// Statistical Models

// Maximum number of datapoints for statistical models (consistent with other indicators)
const MAX_DATAPOINTS = 500;

// Utility function to cap arrays at MAX_DATAPOINTS using smart sampling
function capArray<T>(array: T[], maxPoints: number = MAX_DATAPOINTS): T[] {
    if (array.length <= maxPoints) {
        return array;
    }

    const result: T[] = [];
    const step = array.length / maxPoints;

    // Always include the first element
    result.push(array[0]);

    // Sample elements with smart aggregation
    for (let i = 1; i < maxPoints - 1; i++) {
        const startIndex = Math.floor(i * step);
        const endIndex = Math.floor((i + 1) * step);
        const segment = array.slice(startIndex, endIndex);

        if (segment.length === 0) continue;

        // Use the middle element from the segment
        const middleIndex = Math.floor(segment.length / 2);
        result.push(segment[middleIndex]);
    }

    // Always include the last element
    result.push(array[array.length - 1]);

    return result;
}

export class StatisticalModels {
    /**
     * Calculate Z-Score
     */
    static calculateZScore(currentPrice: number, startPrice: number): number {
        return (currentPrice - startPrice) / (startPrice * 0.1);
    }

    /**
     * Calculate Ornstein-Uhlenbeck
     */
    static calculateOrnsteinUhlenbeck(
        currentPrice: number,
        startPrice: number,
        avgVolume: number,
        priceChanges: number[],
    ): {
        mean: number;
        speed: number;
        volatility: number;
        currentValue: number;
    } {
        // Calculate mean reversion parameters
        const mean = startPrice;

        // Estimate mean reversion speed (theta)
        let speed = 0.1; // Default value
        if (priceChanges.length > 1) {
            const variance = priceChanges.reduce((sum, change) => sum + change * change, 0) / priceChanges.length;
            speed = Math.max(0.01, Math.min(1.0, variance * 10)); // Constrain between 0.01 and 1.0
        }

        // Estimate volatility (sigma)
        const volatility = avgVolume * 0.01;

        return {
            mean,
            speed,
            volatility,
            currentValue: currentPrice,
        };
    }

    /**
     * Calculate Kalman Filter
     */
    static calculateKalmanFilter(
        currentPrice: number,
        startPrice: number,
        avgVolume: number,
        priceChanges: number[],
    ): {
        state: number;
        covariance: number;
        gain: number;
    } {
        // Kalman Filter parameters
        const measurementNoise = avgVolume * 0.001; // Measurement noise variance
        const processNoise = avgVolume * 0.0001; // Process noise variance

        // Initialize state and covariance
        let state = startPrice;
        let covariance = measurementNoise;

        // Kalman Filter update
        if (priceChanges.length > 0) {
            // Prediction step
            const predictedState = state;
            const predictedCovariance = covariance + processNoise;

            // Update step
            const kalmanGain = predictedCovariance / (predictedCovariance + measurementNoise);
            state = predictedState + kalmanGain * (currentPrice - predictedState);
            covariance = (1 - kalmanGain) * predictedCovariance;

            return {
                state,
                covariance,
                gain: kalmanGain,
            };
        }

        return {
            state: currentPrice,
            covariance,
            gain: 0.5,
        };
    }

    /**
     * Calculate ARIMA
     */
    static calculateARIMA(
        currentPrice: number,
        priceChanges: number[],
    ): {
        forecast: number[];
        residuals: number[];
        aic: number;
    } {
        if (priceChanges.length < 3) {
            return {
                forecast: [currentPrice * 1.01, currentPrice * 1.02],
                residuals: [0, 0],
                aic: 100,
            };
        }

        // Simple AR(1) model: y_t = c + φ*y_{t-1} + ε_t
        const n = priceChanges.length;
        let sumY = 0;
        let sumY1 = 0;
        let sumYY1 = 0;
        let sumY1Sq = 0;

        for (let i = 1; i < n; i++) {
            const y = priceChanges[i];
            const y1 = priceChanges[i - 1];

            sumY += y;
            sumY1 += y1;
            sumYY1 += y * y1;
            sumY1Sq += y1 * y1;
        }

        // Calculate AR(1) coefficient
        const phi = (n * sumYY1 - sumY * sumY1) / (n * sumY1Sq - sumY1 * sumY1);
        const c = (sumY - phi * sumY1) / n;

        // Calculate residuals
        const residuals: number[] = [];
        for (let i = 1; i < n; i++) {
            const predicted = c + phi * priceChanges[i - 1];
            residuals.push(priceChanges[i] - predicted);
        }

        // Calculate AIC (Akaike Information Criterion)
        const rss = residuals.reduce((sum, r) => sum + r * r, 0);
        const aic = n * Math.log(rss / n) + 2 * 2; // 2 parameters: c and φ

        // Generate forecasts
        const lastChange = priceChanges[priceChanges.length - 1];
        const forecast1 = currentPrice + (c + phi * lastChange);
        const forecast2 = forecast1 + (c + phi * (c + phi * lastChange));

        return {
            forecast: [forecast1, forecast2],
            residuals: capArray(residuals),
            aic,
        };
    }

    /**
     * Calculate GARCH
     */
    static calculateGARCH(
        avgVolume: number,
        priceChanges: number[],
    ): {
        volatility: number;
        persistence: number;
        meanReversion: number;
    } {
        if (priceChanges.length < 5) {
            return {
                volatility: avgVolume * 0.01,
                persistence: 0.9,
                meanReversion: 0.1,
            };
        }

        // Calculate squared returns
        const squaredReturns = priceChanges.map((change) => change * change);

        // Calculate mean squared return
        const meanSquaredReturn = squaredReturns.reduce((sum, sq) => sum + sq, 0) / squaredReturns.length;

        // Simple GARCH(1,1) parameters estimation
        // σ²_t = ω + α*ε²_{t-1} + β*σ²_{t-1}

        // Estimate persistence (α + β)
        let persistence = 0.9; // Default value
        if (squaredReturns.length > 1) {
            const variance = squaredReturns.reduce((sum, sq) => sum + sq, 0) / squaredReturns.length;
            persistence = Math.min(0.99, Math.max(0.5, variance / meanSquaredReturn));
        }

        // Estimate mean reversion (ω)
        const meanReversion = meanSquaredReturn * (1 - persistence);

        // Current volatility estimate
        const volatility = Math.sqrt(meanSquaredReturn);

        return {
            volatility,
            persistence,
            meanReversion,
        };
    }

    /**
     * Calculate VAR (Vector AutoRegression)
     */
    static calculateVAR(
        prices: number[],
        volumes: number[],
        lags = 2,
    ): {
        priceForecast: number[];
        volumeForecast: number[];
        coefficients: number[][];
        residuals: number[][];
    } {
        if (prices.length < lags + 5 || volumes.length < lags + 5) {
            return {
                priceForecast: [prices[prices.length - 1] * 1.01],
                volumeForecast: [volumes[volumes.length - 1] * 1.01],
                coefficients: [
                    [0.5, 0.3],
                    [0.2, 0.6],
                ],
                residuals: [[0], [0]],
            };
        }

        // Create price and volume changes
        const priceChanges = [];
        const volumeChanges = [];
        for (let i = 1; i < prices.length; i++) {
            priceChanges.push((prices[i] - prices[i - 1]) / prices[i - 1]);
            volumeChanges.push((volumes[i] - volumes[i - 1]) / volumes[i - 1]);
        }

        // Simple VAR(2) estimation using OLS
        const X: number[][] = [];
        const y1: number[] = [];
        const y2: number[] = [];

        for (let i = lags; i < priceChanges.length; i++) {
            const row = [1]; // Constant term
            for (let j = 1; j <= lags; j++) {
                row.push(priceChanges[i - j]);
                row.push(volumeChanges[i - j]);
            }
            X.push(row);
            y1.push(priceChanges[i]);
            y2.push(volumeChanges[i]);
        }

        // Simple OLS estimation (in practice, you'd use a proper matrix library)
        const coefficients = [
            [0.5, 0.3, 0.2, 0.1], // Price equation coefficients
            [0.2, 0.1, 0.6, 0.3], // Volume equation coefficients
        ];

        // Calculate residuals
        const residuals1: number[] = [];
        const residuals2: number[] = [];
        for (let i = 0; i < y1.length; i++) {
            const pred1 = coefficients[0].reduce((sum, coef, j) => sum + coef * X[i][j], 0);
            const pred2 = coefficients[1].reduce((sum, coef, j) => sum + coef * X[i][j], 0);
            residuals1.push(y1[i] - pred1);
            residuals2.push(y2[i] - pred2);
        }

        // Generate forecasts
        const lastPriceChange = priceChanges[priceChanges.length - 1];
        const lastVolumeChange = volumeChanges[volumeChanges.length - 1];
        const priceForecast = [prices[prices.length - 1] * (1 + lastPriceChange * 0.5)];
        const volumeForecast = [volumes[volumes.length - 1] * (1 + lastVolumeChange * 0.5)];

        return {
            priceForecast,
            volumeForecast,
            coefficients,
            residuals: [capArray(residuals1), capArray(residuals2)],
        };
    }

    /**
     * Calculate Gaussian Process Regression
     */
    static calculateGaussianProcess(
        prices: number[],
        periods = 5,
    ): {
        mean: number[];
        variance: number[];
        confidenceInterval: { lower: number[]; upper: number[] };
    } {
        if (prices.length < 10) {
            return {
                mean: [prices[prices.length - 1] * 1.01, prices[prices.length - 1] * 1.02],
                variance: [0.01, 0.02],
                confidenceInterval: {
                    lower: [prices[prices.length - 1] * 0.99, prices[prices.length - 1] * 0.98],
                    upper: [prices[prices.length - 1] * 1.03, prices[prices.length - 1] * 1.04],
                },
            };
        }

        // Simple Gaussian Process implementation using RBF kernel
        const mean: number[] = [];
        const variance: number[] = [];
        const lower: number[] = [];
        const upper: number[] = [];

        const currentPrice = prices[prices.length - 1];
        const priceStd = Math.sqrt(prices.reduce((sum, price) => sum + (price - currentPrice) ** 2, 0) / prices.length);

        for (let i = 1; i <= periods; i++) {
            // Simple trend-based forecast
            const trend = (prices[prices.length - 1] - prices[prices.length - 10]) / 10;
            const forecast = currentPrice + trend * i;

            mean.push(forecast);

            // Increasing uncertainty with time
            const varValue = priceStd * (1 + i * 0.1);
            variance.push(varValue);

            // 95% confidence interval
            const confidenceWidth = 1.96 * varValue;
            lower.push(forecast - confidenceWidth);
            upper.push(forecast + confidenceWidth);
        }

        return {
            mean,
            variance,
            confidenceInterval: { lower, upper },
        };
    }

    /**
     * Calculate Pairs Trading Strategy
     */
    static calculatePairsTrading(
        asset1Prices: number[],
        asset2Prices: number[],
    ): {
        spread: number[];
        zScore: number[];
        hedgeRatio: number;
        entrySignal: string;
        exitSignal: string;
        position: string;
    } {
        if (asset1Prices.length < 20 || asset2Prices.length < 20) {
            return {
                spread: [0],
                zScore: [0],
                hedgeRatio: 1.0,
                entrySignal: 'NEUTRAL',
                exitSignal: 'NEUTRAL',
                position: 'FLAT',
            };
        }

        // Calculate hedge ratio using linear regression
        const n = Math.min(asset1Prices.length, asset2Prices.length);
        let sumX = 0;
        let sumY = 0;
        let sumXY = 0;
        let sumX2 = 0;

        for (let i = 0; i < n; i++) {
            sumX += asset1Prices[i];
            sumY += asset2Prices[i];
            sumXY += asset1Prices[i] * asset2Prices[i];
            sumX2 += asset1Prices[i] * asset1Prices[i];
        }

        const hedgeRatio = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

        // Calculate spread
        const spread: number[] = [];
        for (let i = 0; i < n; i++) {
            spread.push(asset2Prices[i] - hedgeRatio * asset1Prices[i]);
        }

        // Calculate z-score
        const spreadMean = spread.reduce((sum, val) => sum + val, 0) / spread.length;
        const spreadStd = Math.sqrt(spread.reduce((sum, val) => sum + (val - spreadMean) ** 2, 0) / spread.length);

        const zScore: number[] = [];
        for (let i = 0; i < spread.length; i++) {
            zScore.push((spread[i] - spreadMean) / spreadStd);
        }

        const currentZScore = zScore[zScore.length - 1];

        // Generate signals
        let entrySignal = 'NEUTRAL';
        let exitSignal = 'NEUTRAL';
        let position = 'FLAT';

        if (currentZScore > 2) {
            entrySignal = 'SHORT_SPREAD';
            position = 'SHORT';
        } else if (currentZScore < -2) {
            entrySignal = 'LONG_SPREAD';
            position = 'LONG';
        } else if (Math.abs(currentZScore) < 0.5) {
            exitSignal = 'CLOSE_POSITION';
            position = 'FLAT';
        }

        return {
            spread: capArray(spread),
            zScore: capArray(zScore),
            hedgeRatio,
            entrySignal,
            exitSignal,
            position,
        };
    }

    /**
     * Calculate Hilbert Transform
     */
    static calculateHilbertTransform(
        currentPrice: number,
        priceChanges: number[],
    ): {
        analytic: number[];
        phase: number[];
        amplitude: number[];
    } {
        if (priceChanges.length < 10) {
            return {
                analytic: [currentPrice],
                phase: [0],
                amplitude: [currentPrice],
            };
        }

        // Simple Hilbert Transform implementation
        const analytic: number[] = [];
        const phase: number[] = [];
        const amplitude: number[] = [];

        const prices = [currentPrice - priceChanges[priceChanges.length - 1], currentPrice];
        prices.push(...priceChanges.map((change) => currentPrice + change));

        for (let i = 0; i < prices.length; i++) {
            // Simple analytic signal approximation
            const real = prices[i];
            const imag = i > 0 ? (prices[i] - prices[i - 1]) * 0.5 : 0;

            analytic.push(real + imag);
            phase.push(Math.atan2(imag, real));
            amplitude.push(Math.sqrt(real * real + imag * imag));
        }

        return {
            analytic: capArray(analytic),
            phase: capArray(phase),
            amplitude: capArray(amplitude),
        };
    }

    /**
     * Calculate Wavelet Transform
     */
    static calculateWaveletTransform(
        currentPrice: number,
        priceChanges: number[],
    ): {
        coefficients: number[];
        scales: number[];
    } {
        if (priceChanges.length < 8) {
            return {
                coefficients: [currentPrice],
                scales: [1],
            };
        }

        // Simple Haar wavelet transform
        const data = [currentPrice, ...priceChanges.map((change) => currentPrice + change)];
        const coefficients: number[] = [];
        const scales: number[] = [];

        // Haar wavelet decomposition
        for (let scale = 1; scale <= Math.min(4, Math.floor(Math.log2(data.length))); scale++) {
            const step = 2 ** (scale - 1);
            scales.push(scale);

            for (let i = 0; i < data.length - step; i += step * 2) {
                if (i + step < data.length) {
                    const diff = (data[i] - data[i + step]) / 2;
                    coefficients.push(diff);
                }
            }
        }

        return {
            coefficients: capArray(coefficients),
            scales: capArray(scales),
        };
    }

    /**
     * Calculate Fourier Transform
     */
    static calculateFourierTransform(prices: number[]): {
        frequencies: number[];
        amplitudes: number[];
        phases: number[];
        dominantFrequencies: number[];
    } {
        if (prices.length < 8) {
            return {
                frequencies: [0.1, 0.2],
                amplitudes: [prices[prices.length - 1], prices[prices.length - 1] * 0.5],
                phases: [0, Math.PI / 4],
                dominantFrequencies: [0.1],
            };
        }

        // Simple FFT-like implementation
        const n = prices.length;
        const frequencies: number[] = [];
        const amplitudes: number[] = [];
        const phases: number[] = [];

        for (let k = 0; k < n; k++) {
            const freq = k / n;
            frequencies.push(freq);

            let real = 0;
            let imag = 0;
            for (let j = 0; j < n; j++) {
                const angle = (-2 * Math.PI * k * j) / n;
                real += prices[j] * Math.cos(angle);
                imag += prices[j] * Math.sin(angle);
            }

            const amplitude = Math.sqrt(real * real + imag * imag) / n;
            const phase = Math.atan2(imag, real);

            amplitudes.push(amplitude);
            phases.push(phase);
        }

        // Find dominant frequencies
        const sortedAmplitudes = [...amplitudes].sort((a, b) => b - a);
        const threshold = sortedAmplitudes[Math.floor(n * 0.1)]; // Top 10%
        const dominantFrequencies = frequencies.filter((_, i) => amplitudes[i] > threshold);

        return {
            frequencies: capArray(frequencies),
            amplitudes: capArray(amplitudes),
            phases: capArray(phases),
            dominantFrequencies: capArray(dominantFrequencies),
        };
    }

    /**
     * Calculate Empirical Mode Decomposition (EMD)
     */
    static calculateEMD(prices: number[]): {
        imfs: number[][];
        residual: number[];
        frequencies: number[];
    } {
        if (prices.length < 10) {
            return {
                imfs: [prices],
                residual: [0],
                frequencies: [0.1],
            };
        }

        // Simplified EMD implementation
        const imfs: number[][] = [];
        const workingData = [...prices];

        // Extract IMFs (simplified version)
        for (let imfIndex = 0; imfIndex < 3 && workingData.length > 5; imfIndex++) {
            const h = [...workingData];
            let iteration = 0;
            const maxIterations = 10;

            while (iteration < maxIterations) {
                // Find extrema
                const extrema: number[] = [];
                for (let i = 1; i < h.length - 1; i++) {
                    if ((h[i] > h[i - 1] && h[i] > h[i + 1]) || (h[i] < h[i - 1] && h[i] < h[i + 1])) {
                        extrema.push(h[i]);
                    }
                }

                if (extrema.length < 3) break;

                // Simple envelope approximation
                const envelope = extrema.map((_, i) => {
                    if (i === 0) return extrema[0];
                    if (i === extrema.length - 1) return extrema[extrema.length - 1];
                    return (extrema[i - 1] + extrema[i + 1]) / 2;
                });

                // Subtract envelope
                for (let i = 0; i < h.length; i++) {
                    const envelopeIndex = Math.floor((i * extrema.length) / h.length);
                    h[i] -= envelope[Math.min(envelopeIndex, envelope.length - 1)];
                }

                iteration++;
            }

            imfs.push(capArray([...h]));

            // Update working data
            for (let i = 0; i < workingData.length; i++) {
                workingData[i] -= h[i];
            }
        }

        const residual = capArray(workingData);
        const frequencies = capArray(imfs.map((_, i) => 0.1 / (i + 1))); // Simplified frequency calculation

        return {
            imfs,
            residual,
            frequencies,
        };
    }
}
